import React from "react";
import { BtnOrange, BtnOutline } from "../ui/Buttons";
import { HERO_BARS } from "../../data";

const HeroDashboard = () => (
  <div className="relative w-full max-w-md animate-float">
    <div className="glass rounded-2xl p-6 shadow-[0_30px_80px_rgba(0,0,0,.45)]">
      {/* Title bar */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex gap-1.5">
          <span className="w-3 h-3 rounded-full bg-red-400" />
          <span className="w-3 h-3 rounded-full bg-yellow-400" />
          <span className="w-3 h-3 rounded-full bg-emerald-400" />
        </div>
        <span className="text-white/40 text-xs font-head">RetailIQ™ Live</span>
        <span className="text-xs text-emerald-400 font-head font-semibold flex items-center gap-1">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulseDot inline-block" />
          Live
        </span>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-3 gap-3 mb-5">
        {[
          { v: "2.4M",  l: "Footfall",    c: "+12.3%", up: true  },
          { v: "₨84K", l: "Avg Tx",       c: "+8.7%",  up: true  },
          { v: "94%",   l: "Stock Health", c: "−1.2%",  up: false },
        ].map(k => (
          <div key={k.l} className="bg-white/6 border border-white/8 rounded-xl p-3">
            <div className="font-head font-bold text-xl text-white">{k.v}</div>
            <div className="text-white/40 text-xs mt-1">{k.l}</div>
            <div className={`text-xs font-semibold mt-0.5 ${k.up ? "text-emerald-400" : "text-red-400"}`}>{k.c}</div>
          </div>
        ))}
      </div>

      {/* Bar chart */}
      <div className="text-white/35 text-xs font-head mb-3">Weekly Sales Performance</div>
      <div className="space-y-2">
        {HERO_BARS.map((b) => (
          <div key={b.day} className="flex items-center gap-3">
            <span className="text-white/40 text-xs w-8 shrink-0">{b.day}</span>
            <div className="flex-1 h-2 bg-white/8 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full bar-fill"
                style={{
                  width: `${b.pct}%`,
                  background: b.orange
                    ? "linear-gradient(90deg,#f57c00,#ff9a2e)"
                    : "linear-gradient(90deg,#1e56d9,#2b7fff)",
                }}
              />
            </div>
            <span className="text-white/40 text-xs w-8 text-right shrink-0">{b.pct}%</span>
          </div>
        ))}
      </div>
    </div>

    {/* Floating badges */}
    <div className="absolute -top-5 -left-8 bg-white rounded-2xl shadow-[0_16px_40px_rgba(0,0,0,.25)] p-3.5 flex items-center gap-3 float-badge-1">
      <div className="w-9 h-9 bg-emerald-50 rounded-xl flex items-center justify-center shrink-0">
        <svg viewBox="0 0 20 20" fill="#16a34a" className="w-5 h-5">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"/>
        </svg>
      </div>
      <div>
        <p className="font-head font-semibold text-slate-800 text-xs">AI Accuracy</p>
        <p className="font-head font-bold text-emerald-600 text-sm">99.2% Detection</p>
      </div>
    </div>

    <div className="absolute -bottom-3 -right-6 bg-white rounded-2xl shadow-[0_16px_40px_rgba(0,0,0,.25)] p-3.5 flex items-center gap-3 float-badge-2">
      <div className="w-9 h-9 bg-orange-50 rounded-xl flex items-center justify-center shrink-0">
        <svg viewBox="0 0 20 20" fill="#f57c00" className="w-5 h-5">
          <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z"/>
        </svg>
      </div>
      <div>
        <p className="font-head font-semibold text-slate-800 text-xs">Revenue Growth</p>
        <p className="font-head font-bold text-orange-500 text-sm">+38% This Quarter</p>
      </div>
    </div>
  </div>
);

const HeroSection = ({ navigate }) => (
  <section className="min-h-screen bg-gradient-to-br from-slate-950 via-[#0d2151] to-[#0e2660] relative overflow-hidden flex items-center pt-16">
    {/* Background effects */}
    <div className="hero-grid-bg absolute inset-0 pointer-events-none" />
    <div
      className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full pointer-events-none"
      style={{ background: "radial-gradient(circle, rgba(43,127,255,.18) 0%, transparent 70%)" }}
    />
    <div
      className="absolute bottom-0 left-1/4 w-[400px] h-[400px] rounded-full pointer-events-none"
      style={{ background: "radial-gradient(circle, rgba(245,124,0,.10) 0%, transparent 70%)" }}
    />

    <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 py-20 grid lg:grid-cols-2 gap-14 items-center w-full">
      {/* Left — copy */}
      <div className="animate-fadeUp">
        <div className="inline-flex items-center gap-2 bg-blue-500/15 border border-blue-400/30 text-blue-300 text-xs font-head font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-6">
          <span className="w-1.5 h-1.5 rounded-full bg-blue-300 animate-pulseDot inline-block" />
          AI-Powered Innovation
        </div>

        <h1 className="font-head font-black text-5xl md:text-6xl lg:text-[4rem] text-white leading-[1.05] tracking-tight mb-5">
          Empowering<br />
          Industry with{" "}
          <span className="gradient-text">AI-Driven</span>
          <br />Innovation
        </h1>

        <p className="text-white/65 text-lg leading-relaxed mb-9 max-w-xl">
          We develop intelligent software solutions that transform operations,
          drive efficiency, and deliver measurable results across industries.
        </p>

        <div className="flex flex-wrap gap-3 mb-12">
          <BtnOrange onClick={() => navigate("contact")}>
            <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
              <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z"/>
            </svg>
            Get a Free Consultation
          </BtnOrange>
          <BtnOutline onClick={() => navigate("products")}>
            Explore Our Solutions
            <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
              <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd"/>
            </svg>
          </BtnOutline>
        </div>

        {/* Stats */}
        <div className="flex gap-10 flex-wrap">
          {[
            { num: "50+", label: "Projects Delivered" },
            { num: "30+", label: "Enterprise Clients" },
            { num: "98%", label: "Client Satisfaction" },
          ].map(s => (
            <div key={s.label}>
              <div className="font-head font-black text-3xl text-white">{s.num}</div>
              <div className="text-white/40 text-sm mt-0.5">{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Right — dashboard */}
      <div className="hidden lg:flex justify-center items-center">
        <HeroDashboard />
      </div>
    </div>
  </section>
);

export default HeroSection;
